/**
 * Local-only persistence for the public content-submission contributor info.
 *
 * Stored in the contributor's own browser (localStorage), never sent to our
 * servers. Lets recurring contributors (e.g. employees submitting weekly
 * photos) skip re-typing their name + email. Cleared after 30 days of
 * inactivity to handle shared devices gracefully.
 */

const STORAGE_KEY = 'lomavis.collectContent.contributor';
const TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

export type RememberedContributor = {
  name: string;
  email: string;
  licenceInfo: string;
  /**
   * Timestamp (ms since epoch) when the contributor accepted the ToS on a
   * previous submission. The 30-day inactivity TTL handles staleness — once
   * the whole record expires, the contributor goes through the full form
   * with a fresh ToS checkbox.
   */
  toSAcceptedAt: number;
  savedAt: number;
};

export function readRememberedContributor(): RememberedContributor | null {
  if (typeof window === 'undefined' || !window.localStorage) {
    return null;
  }
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as RememberedContributor;
    if (
      !parsed ||
      typeof parsed !== 'object' ||
      !parsed.savedAt ||
      Date.now() - parsed.savedAt > TTL_MS ||
      !parsed.name ||
      !parsed.email
    ) {
      window.localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    // Older records (pre-ToS-persistence) won't have toSAcceptedAt — fall back
    // to savedAt, which is when they previously consented + submitted.
    return {
      ...parsed,
      toSAcceptedAt: parsed.toSAcceptedAt || parsed.savedAt
    };
  } catch {
    return null;
  }
}

export function writeRememberedContributor(
  data: Omit<RememberedContributor, 'savedAt' | 'toSAcceptedAt'> & {
    /** Optional explicit ToS-acceptance timestamp. Defaults to now. */
    toSAcceptedAt?: number;
  }
): void {
  if (typeof window === 'undefined' || !window.localStorage) {
    return;
  }
  try {
    const now = Date.now();
    window.localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        name: data.name,
        email: data.email,
        licenceInfo: data.licenceInfo,
        toSAcceptedAt: data.toSAcceptedAt ?? now,
        savedAt: now
      })
    );
  } catch {
    // localStorage may throw (quota full, denied, private mode) — silent fail
  }
}

export function clearRememberedContributor(): void {
  if (typeof window === 'undefined' || !window.localStorage) {
    return;
  }
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}
